/**
 * CampaignList Page Component
 * Displays the campaigns in a minimalist table layout with search and pagination
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, LayoutDashboard, Pencil, Trash2, MoreHorizontal } from 'lucide-react'
import { Campaign } from '../../types'
import { campaignApi, PaginationMeta, CreateCampaignRequest } from '../../lib/api/campaigns'
import { CreateCampaignModal } from './CreateCampaignModal'

export function CampaignList() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  // Build the back-target so navigating into a campaign and clicking back
  // returns to the same list view (preserving page & search query).
  const buildFromUrl = () => `${location.pathname}${location.search}`
  
  // Get initial values from URL
  const initialPage = parseInt(searchParams.get('page') || '1', 10)
  const initialQuery = searchParams.get('q') || ''
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false)
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [isCreateCampaignModalOpen, setIsCreateCampaignModalOpen] = useState(false)
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false)
  
  // Action menu state
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null)
  const actionMenuRef = useRef<HTMLDivElement>(null)
  
  // Delete confirmation modal state
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Edit modal state
  const [campaignToEdit, setCampaignToEdit] = useState<Campaign | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationMeta>({
    current_page: initialPage,
    total_pages: 1,
    total_count: 0
  })
  
  // Debounce timer for search
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // Track if initial load has happened
  const hasLoadedRef = useRef(false)

  /**
   * Update URL params
   */
  const updateUrlParams = useCallback((page: number, query: string) => {
    const params = new URLSearchParams()
    if (page > 1) params.set('page', page.toString())
    if (query) params.set('q', query)
    setSearchParams(params, { replace: true })
  }, [setSearchParams])

  /**
   * Load campaigns from API
   */
  const loadCampaigns = useCallback(async (
    page: number = 1,
    query: string = ''
  ) => {
    setIsLoadingCampaigns(true)
    try {
      const { campaigns: data, meta } = await campaignApi.getCampaigns({
        page,
        q: query || undefined,
        search_by: query ? 'Campaigns' : undefined
      })
      
      setCampaigns(data)
      setPagination(meta)
      // Update URL with current page and search
      updateUrlParams(page, query)
    } catch (error) {
      console.error('Failed to load campaigns:', error)
      toast.error('Failed to load campaigns')
    } finally {
      setIsLoadingCampaigns(false)
    }
  }, [updateUrlParams])

  // Initial load from URL params
  useEffect(() => {
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    loadCampaigns(initialPage, initialQuery)
  }, [loadCampaigns, initialPage, initialQuery])

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    
    searchTimerRef.current = setTimeout(() => {
      loadCampaigns(1, value)
    }, 300)
  }

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.total_pages) return
    loadCampaigns(newPage, searchQuery)
  }

  /**
   * Handle create campaign
   */
  const handleCreateCampaign = async (data: CreateCampaignRequest) => {
    setIsCreatingCampaign(true)
    try {
      const newCampaign = await campaignApi.createCampaign(data)
      setCampaigns(prev => [newCampaign, ...prev])
      setPagination(prev => ({
        ...prev,
        total_count: prev.total_count + 1
      }))
      toast.success('Campaign created successfully')
      setIsCreateCampaignModalOpen(false)
    } catch (error) {
      console.error('Failed to create campaign:', error)
      toast.error('Failed to create campaign')
      throw error
    } finally {
      setIsCreatingCampaign(false)
    }
  }

  /**
   * Open delete confirmation modal
   */
  const handleDeleteClick = (campaign: Campaign) => {
    setCampaignToDelete(campaign)
    setOpenActionMenu(null)
  }

  /**
   * Confirm and execute delete
   */
  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return
    
    setIsDeleting(true)
    try {
      await campaignApi.deleteCampaign(campaignToDelete.id)
      setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete.id))
      setPagination(prev => ({
        ...prev,
        total_count: Math.max(0, prev.total_count - 1)
      }))
      toast.success('Campaign deleted successfully')
      setCampaignToDelete(null)
    } catch (error) {
      console.error('Failed to delete campaign:', error)
      toast.error('Failed to delete campaign')
    } finally {
      setIsDeleting(false)
    }
  }

  /**
   * Open edit modal
   */
  const handleEditClick = (campaign: Campaign) => {
    setCampaignToEdit(campaign)
    setOpenActionMenu(null)
  }

  /**
   * Handle update campaign
   */
  const handleUpdateCampaign = async (data: { name: string; description?: string }) => {
    if (!campaignToEdit) return
    
    setIsUpdating(true)
    try {
      const updated = await campaignApi.updateCampaign(campaignToEdit.id, data)
      setCampaigns(prev => prev.map(c => c.id === updated.id ? updated : c))
      toast.success('Campaign updated successfully')
      setCampaignToEdit(null)
    } catch (error) {
      console.error('Failed to update campaign:', error)
      toast.error('Failed to update campaign')
      throw error
    } finally {
      setIsUpdating(false)
    }
  }

  /**
   * Format date to readable string
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatFullDateTime = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  /**
   * Filter campaigns by active status
   */

  /**
   * Generate page numbers to display
   */
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const { current_page, total_pages } = pagination
    const pages: (number | 'ellipsis')[] = []
    
    if (total_pages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)
      
      if (current_page > 3) {
        pages.push('ellipsis')
      }
      
      // Show pages around current page
      const start = Math.max(2, current_page - 1)
      const end = Math.min(total_pages - 1, current_page + 1)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      if (current_page < total_pages - 2) {
        pages.push('ellipsis')
      }
      
      // Always show last page
      pages.push(total_pages)
    }
    
    return pages
  }

  /**
   * Render pagination controls
   */
  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null

    const pageNumbers = getPageNumbers()
    const itemsPerPage = 10 // Default items per page
    const startItem = (pagination.current_page - 1) * itemsPerPage + 1
    const endItem = Math.min(pagination.current_page * itemsPerPage, pagination.total_count)

    return (
      <div className="flex items-center justify-between py-4 px-6">
        {/* Results info */}
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing <span className="font-medium text-gray-900 dark:text-white">{startItem}</span> to{' '}
          <span className="font-medium text-gray-900 dark:text-white">{endItem}</span> of{' '}
          <span className="font-medium text-gray-900 dark:text-white">{pagination.total_count}</span> results
        </div>

        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.current_page <= 1 || isLoadingCampaigns}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="First page"
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>

          {/* Previous page */}
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page <= 1 || isLoadingCampaigns}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
          
          {/* Page numbers */}
          <div className="flex items-center gap-1 mx-1">
            {pageNumbers.map((page, index) => (
              page === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-400 dark:text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={isLoadingCampaigns}
                  className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                    pagination.current_page === page
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {page}
                </button>
              )
            ))}
          </div>
          
          {/* Next page */}
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page >= pagination.total_pages || isLoadingCampaigns}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
            title="Next page"
          >
            <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>

          {/* Last page */}
          <button
            onClick={() => handlePageChange(pagination.total_pages)}
            disabled={pagination.current_page >= pagination.total_pages || isLoadingCampaigns}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Last page"
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-950">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Title */}
            <h1 className="text-xl font-medium text-gray-900 dark:text-white">
              Campaigns
            </h1>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/?new=true')}
                className="flex items-center gap-2 px-4 py-1.5 border border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Create with AI
              </button>
              <button
                onClick={() => setIsCreateCampaignModalOpen(true)}
                className="flex items-center gap-2 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Campaign
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6">
          {/* Sticky Search Bar */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-950 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-end">
              {/* Search Bar */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-64 px-3 py-1.5 pl-9 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600 focus:border-gray-300 dark:focus:border-gray-600 transition-colors"
                />
                <svg 
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
          {isLoadingCampaigns && campaigns.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading campaigns...</p>
              </div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 mb-1">
                {searchQuery 
                  ? 'No campaigns match your search'
                  : 'No campaigns yet'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {searchQuery
                  ? 'Try adjusting your search terms'
                  : 'Click "New Campaign" to create your first campaign'}
              </p>
            </div>
          ) : (
            <>
              {/* Table Header - Sticky below controls */}
              <div className="sticky top-[53px] z-10 grid grid-cols-12 gap-4 px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800/50 bg-white dark:bg-gray-950">
                <div className="col-span-1">ID</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Advertiser</div>
                <div className="col-span-2">Agency</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors group"
                  >
                    {/* ID - Clickable to view */}
                    <div className="col-span-1">
                      <button
                        onClick={() => navigate(`/campaigns/${campaign.id}`, { state: { from: buildFromUrl() } })}
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition-colors"
                      >
                        {campaign.id}
                      </button>
                    </div>

                    {/* Name */}
                    <div className="col-span-3 min-w-0">
                      <button
                        onClick={() => navigate(`/campaigns/${campaign.id}`, { state: { from: buildFromUrl() } })}
                        className="text-sm font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 truncate block text-left transition-colors max-w-full"
                        title={campaign.name || `Campaign ${campaign.id}`}
                      >
                        {campaign.name || `Campaign ${campaign.id}`}
                      </button>
                      {campaign.description && (
                        <span 
                          className="text-xs text-gray-500 dark:text-gray-500 truncate block mt-0.5"
                          title={campaign.description}
                        >
                          {campaign.description}
                        </span>
                      )}
                    </div>

                    {/* Advertiser */}
                    <div className="col-span-2 min-w-0">
                      <span 
                        className="text-sm text-gray-700 dark:text-gray-300 truncate block"
                        title={campaign.advertiser?.name}
                      >
                        {campaign.advertiser?.name || '-'}
                      </span>
                    </div>

                    {/* Agency */}
                    <div className="col-span-2 min-w-0">
                      <span 
                        className="text-sm text-gray-700 dark:text-gray-300 truncate block"
                        title={campaign.agency?.name}
                      >
                        {campaign.agency?.name || '-'}
                      </span>
                    </div>

                    {/* Created Date */}
                    <div className="col-span-2">
                      <span 
                        className="text-sm text-gray-600 dark:text-gray-400 cursor-default"
                        title={formatFullDateTime(campaign.created_at)}
                      >
                        {formatDate(campaign.created_at)}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="col-span-1 flex justify-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        campaign.active
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                        {campaign.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex justify-end relative" ref={openActionMenu === campaign.id ? actionMenuRef : null}>
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === campaign.id ? null : campaign.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Campaign actions"
                      >
                        <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </button>

                      {/* Action Dropdown */}
                      {openActionMenu === campaign.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                          <button
                            onClick={() => {
                              navigate(`/campaigns/${campaign.id}`, { state: { from: buildFromUrl() } })
                              setOpenActionMenu(null)
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </button>
                          <button
                            onClick={() => {
                              navigate(`/dashboard/campaign/${campaign.id}`)
                              setOpenActionMenu(null)
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            <LayoutDashboard className="h-4 w-4" />
                            Dashboard
                          </button>
                          <button
                            onClick={() => handleEditClick(campaign)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </button>
                          <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                          <button
                            onClick={() => handleDeleteClick(campaign)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </>
          )}
        </div>
      </div>

      {/* Sticky Pagination at Bottom */}
      {pagination.total_pages > 1 && (
        <div className="flex-shrink-0 bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
          {renderPagination()}
        </div>
      )}

      {/* Create Campaign Modal */}
      <CreateCampaignModal
        isOpen={isCreateCampaignModalOpen}
        onClose={() => setIsCreateCampaignModalOpen(false)}
        onSubmit={handleCreateCampaign}
        isLoading={isCreatingCampaign}
      />

      {/* Edit Campaign Modal */}
      <CreateCampaignModal
        isOpen={!!campaignToEdit}
        onClose={() => setCampaignToEdit(null)}
        onSubmit={handleUpdateCampaign}
        isLoading={isUpdating}
        campaign={campaignToEdit}
      />

      {/* Delete Confirmation Modal */}
      {campaignToDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isDeleting && setCampaignToDelete(null)}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Campaign
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">"{campaignToDelete.name || `Campaign ${campaignToDelete.id}`}"</span>?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCampaignToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCampaign}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
